import React, { useState, useEffect, useCallback } from 'react';
import { getSocket } from '../../utils/socketClient';
import {
  getMyProfile, setAvailability, acceptOrder, declineOrder,
  startSession, stopSession, confirmCompletion, getMyOrders, getPendingOffer
} from '../../services/therapistService';
import { submitRating } from '../../services/ratingService';

const ACTIVE_STATUSES = ['assigned', 'in_session', 'completed_pending_confirmation'];

const OfferInbox = () => {
  const [profile, setProfile] = useState(null);
  const [offer, setOffer] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [clientScore, setClientScore] = useState(5);
  const [flagAsThreat, setFlagAsThreat] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  const loadActiveOrder = useCallback(async () => {
    const orders = await getMyOrders();
    const active = orders.find(o => ACTIVE_STATUSES.includes(o.status));
    setActiveOrder(active || null);
  }, []);

  useEffect(() => {
    getMyProfile().then(setProfile);
    loadActiveOrder();
    // A therapist may open the dashboard after an offer was already broadcast
    // over the socket, so check for one that's still pending on mount too.
    getPendingOffer().then(existing => { if (existing) setOffer(existing); });

    const socket = getSocket();
    socket.connect();

    const onOffer = (data) => setOffer(data);
    const onWithdrawn = (data) => {
      setOffer(prev => (prev && prev.orderId === data.orderId ? null : prev));
    };

    socket.on('order:offer', onOffer);
    socket.on('order:offer:withdrawn', onWithdrawn);

    return () => {
      socket.off('order:offer', onOffer);
      socket.off('order:offer:withdrawn', onWithdrawn);
    };
  }, [loadActiveOrder]);

  const toggleAvailability = async () => {
    setBusy(true);
    try {
      const next = profile.availabilityStatus === 'online' ? 'offline' : 'online';
      const updated = await setAvailability(next);
      setProfile(updated);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update availability');
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = async () => {
    setBusy(true);
    try {
      await acceptOrder(offer.orderId);
      setOffer(null);
      await loadActiveOrder();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not accept order');
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    setBusy(true);
    try {
      await declineOrder(offer.orderId);
      setOffer(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not decline order');
    } finally {
      setBusy(false);
    }
  };

  const handleSessionAction = async (action) => {
    setBusy(true);
    try {
      let updated;
      if (action === 'start') updated = await startSession(activeOrder._id);
      else if (action === 'stop') updated = await stopSession(activeOrder._id);
      setActiveOrder(updated);
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRateAndConfirm = async () => {
    setBusy(true);
    setError('');
    try {
      if (!ratingSubmitted) {
        await submitRating({ orderId: activeOrder._id, score: clientScore, flaggedAsThreat: flagAsThreat });
        setRatingSubmitted(true);
      }
      const updated = await confirmCompletion(activeOrder._id);
      if (updated.status === 'completed_confirmed') {
        setActiveOrder(null);
        setRatingSubmitted(false);
        setClientScore(5);
        setFlagAsThreat(false);
      } else {
        setActiveOrder(updated);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const renderLocation = (order) => {
    const clientName = order.clientId ? `${order.clientId.firstName} ${order.clientId.lastName}` : 'Client';
    const clientPhone = order.clientId?.phone;

    if (order.venueType === 'client_location' && order.clientLocation) {
      const { address, zone, notes } = order.clientLocation;
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || '')}`;
      return (
        <div className="text-sm space-y-1 bg-white/60 rounded-lg p-3">
          <p className="font-medium">Client: {clientName}{clientPhone && (
            <> — <a href={`tel:${clientPhone}`} className="text-blue-600 underline">{clientPhone}</a></>
          )}</p>
          <p>Address: {address}{zone && ` (${zone.name}, ${zone.city})`}</p>
          {notes && <p>Notes: {notes}</p>}
          <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">
            Open in Google Maps
          </a>
        </div>
      );
    }

    if (order.venueType === 'partner_venue' && order.partnerVenue?.venueId) {
      const venue = order.partnerVenue.venueId;
      const room = venue.rooms?.find(r => r._id === order.partnerVenue.roomId);
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address || venue.businessName)}`;
      return (
        <div className="text-sm space-y-1 bg-white/60 rounded-lg p-3">
          <p className="font-medium">Client: {clientName}{clientPhone && (
            <> — <a href={`tel:${clientPhone}`} className="text-blue-600 underline">{clientPhone}</a></>
          )}</p>
          <p>Venue: {venue.businessName}{room && ` — ${room.name}`}</p>
          <p>Address: {venue.address}</p>
          <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">
            Open in Google Maps
          </a>
        </div>
      );
    }

    return null;
  };

  if (!profile) return <p>Loading...</p>;

  if (profile.verification.status !== 'approved') {
    return <p className="text-gray-600">Your profile must be approved before you can go online and receive orders.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white p-4 rounded-lg shadow flex items-center justify-between">
        <span>
          Status: <span className="font-semibold">{profile.availabilityStatus}</span>
        </span>
        <button onClick={toggleAvailability} disabled={busy || profile.availabilityStatus === 'on_session'}
          className="bg-black text-white px-4 py-2 rounded-lg disabled:opacity-50">
          {profile.availabilityStatus === 'online' ? 'Go offline' : 'Go online'}
        </button>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}

      {offer && (
        <div className="bg-yellow-50 border border-yellow-300 p-4 rounded-lg space-y-2">
          <p className="font-semibold">New order offer</p>
          <p>{offer.massageType} — {offer.venueType === 'partner_venue' ? 'Partner venue' : "Client's location"}</p>
          <p>{new Date(offer.scheduledDate).toLocaleDateString()} at {offer.scheduledTime}</p>
          <p>You earn: ₦{offer.pricing.therapistCut.toLocaleString()}</p>
          <div className="flex gap-2">
            <button onClick={handleAccept} disabled={busy} className="bg-green-600 text-white px-4 py-2 rounded-lg">
              Accept
            </button>
            <button onClick={handleDecline} disabled={busy} className="bg-gray-200 px-4 py-2 rounded-lg">
              Decline
            </button>
          </div>
        </div>
      )}

      {activeOrder && (
        <div className="bg-blue-50 border border-blue-300 p-4 rounded-lg space-y-2">
          <p className="font-semibold">Active order</p>
          <p>{activeOrder.massageType} — status: {activeOrder.status}</p>
          {renderLocation(activeOrder)}
          <div className="flex gap-2">
            {activeOrder.status === 'assigned' && (
              <button onClick={() => handleSessionAction('start')} disabled={busy}
                className="bg-black text-white px-4 py-2 rounded-lg">Start session</button>
            )}
            {activeOrder.status === 'in_session' && (
              <button onClick={() => handleSessionAction('stop')} disabled={busy}
                className="bg-black text-white px-4 py-2 rounded-lg">Stop session</button>
            )}
          </div>

          {activeOrder.status === 'completed_pending_confirmation' && (
            <div className="pt-2 border-t border-blue-200 space-y-2">
              <p className="text-sm font-medium">Rate the client</p>
              <select value={clientScore} onChange={(e) => setClientScore(Number(e.target.value))}
                className="border rounded-lg p-2">
                {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{n} star{n > 1 ? 's' : ''}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-red-700">
                <input type="checkbox" checked={flagAsThreat} onChange={(e) => setFlagAsThreat(e.target.checked)} />
                This client behaved in a way that felt unsafe or threatening
              </label>
              <button onClick={handleRateAndConfirm} disabled={busy}
                className="bg-green-600 text-white px-4 py-2 rounded-lg block">
                {ratingSubmitted ? 'Confirm completion' : 'Submit rating & confirm completion'}
              </button>
            </div>
          )}
        </div>
      )}

      {!offer && !activeOrder && profile.availabilityStatus === 'online' && (
        <p className="text-gray-500">You're online. Waiting for order offers...</p>
      )}
    </div>
  );
};

export default OfferInbox;
